import { Box, Button, CircularProgress, Stack, Tooltip } from "@mui/material";
import {
  DataGridPro,
  GridColDef,
  GridRowId,
  GridRowsProp,
  GridColumnVisibilityModel,
  useGridApiRef,
} from "@mui/x-data-grid-pro";
import { differenceInMinutes, format, isSameDay } from "date-fns";
import pluralize from "pluralize";
import { logError, logEvent } from "@src/appV2/lib/analytics";
import { HCF_USER_EVENTS } from "@src/constants/firebaseEvents";
import { ShiftInviteRequestParams, useCreateShiftInvite } from "./useCreateShiftInvite";
import { useSelector } from "react-redux";
import { SessionType } from "@src/modules/interface";
import { UseModalState, useModalState } from "@src/lib/ui-react";
import { zonedTimeToUtc } from "date-fns-tz";
import { v4 as uuidv4 } from "uuid";
import ArrowBackIosNewOutlinedIcon from "@mui/icons-material/ArrowBackIosNewOutlined";
import { useEffect, useState } from "react";
import { useShiftInviteContext } from "./Context/shiftInviteContext";
import { NearbyWorkerSearchResult } from "./Autocomplete/useGetNearbyWorkers";
import { isDefined } from "@clipboard-health/util-ts";
import { message as toast } from "antd";
import { MultiShiftInviteErrorDialog } from "./Dialogs/MultiShiftInviteErrorDialog";
import { toShiftType } from "./utils";
import { useValidateSpendingControl } from "@src/appV2/redesign/SpendingControls/api/useValidateSpendingControl";
import {
  SpendLimitReachedDialog,
  type SpendLimitReachedShift,
} from "@src/appV2/redesign/SpendingControls/components/SpendLimitReachedDialog/SpendLimitReachedDialog";
import { BerlinThemeProvider } from "@src/appV2/redesign/theme/ThemeProvider";
import { Text } from "@src/lib/ui-react";
import { CbhFeatureFlag, useCbhFlag } from "@src/appV2/FeatureFlags";
import { isEmpty } from "lodash";
import { formatCentsAsUsd, formatDollarsAsUsd } from "@src/appV2/lib/Money/utils/currency";
import { formatDate, formatTime } from "@clipboard-health/date-time";
import { ChargeDetails } from "./Context/shiftInviteContext";
export enum Severity {
  INFO = "info",
  WARN = "warn",
  ERROR = "error",
}

export interface ShiftInviteInfo {
  start: Date;
  end: Date;
  name: string;
  details?: { message: string; level: Severity };
  selectedDate?: Date;
  error?: unknown;
}

export interface GroupShiftInvite {
  workplaceId: string;
  agentReq: string;
  tmz: string;
  postShiftToMarketplace: boolean;
}

export type ShiftInviteReviewProps = {
  workerType: string;
  modalState: UseModalState;
  setActiveStep: (step: number) => void;
  selectedWorker?: NearbyWorkerSearchResult;
};

type SendInviteParams = ShiftInviteRequestParams & {
  shiftInfoKey: string;
};

function formatTooltip(lines: string[]): string {
  return lines.map((line) => ` • ${line}`).join("\n");
}

function getDisplayChargeAndTooltip(
  chargeDetails: ChargeDetails,
  customChargeRateBoostIsDefined: boolean
): { displayCharge: string; tooltip: string } | undefined {
  if (chargeDetails.source === "price-api") {
    const displayCharge = formatCentsAsUsd(chargeDetails.charge.amountInMinorUnits);
    if (!customChargeRateBoostIsDefined || !isDefined(chargeDetails.chargeMembers)) {
      return { displayCharge, tooltip: "" };
    }
    const tooltipLines = chargeDetails.chargeMembers.map(
      (member) => `${member.label}: ${formatCentsAsUsd(member.amount.amountInMinorUnits)}`
    );
    return { displayCharge, tooltip: formatTooltip(tooltipLines) };
  }

  const { charge, chargeBreakdown, name: shiftName, start: shiftStart } = chargeDetails;
  if (!isDefined(charge)) {
    return undefined;
  }

  const displayCharge = formatDollarsAsUsd(charge);
  if (
    !customChargeRateBoostIsDefined ||
    !isDefined(shiftName) ||
    !isDefined(shiftStart) ||
    !isDefined(chargeBreakdown) ||
    !isDefined(chargeBreakdown.customRateBoost)
  ) {
    return { displayCharge, tooltip: "" };
  }

  const dayOfWeek = format(new Date(shiftStart), "EEEE");
  const nonBoostedCharge = charge - chargeBreakdown.customRateBoost;
  const tooltipLines = [
    `Charge for ${dayOfWeek} ${shiftName.toUpperCase()} shift: ${formatDollarsAsUsd(
      nonBoostedCharge
    )}`,
    `Custom rate adjustment: ${formatDollarsAsUsd(chargeBreakdown.customRateBoost)}`,
    `Total: ${formatDollarsAsUsd(charge)}`,
  ];
  return { displayCharge, tooltip: formatTooltip(tooltipLines) };
}

export function ShiftInviteReview(props: Readonly<ShiftInviteReviewProps>) {
  const { workerType, modalState, setActiveStep, selectedWorker } = props;
  const { user, admin } = useSelector((state: SessionType) => state.session);

  const { mutateAsync: createShiftInvite, isLoading: isSaving } = useCreateShiftInvite();
  const {
    groupInviteSharedData,
    multipleShiftInfo,
    multipleShiftValidationDetails,
    facilityTimeZone,
    customChargeRateBoost,
    multipleCheckShiftInviteDetails,
    logUserJourney,
    addUserJourneyEvent,
    trackUserJourneyHoverStart,
    addUserJourneyHoverEndEvent,
  } = useShiftInviteContext();
  const customChargeRateBoostIsDefined = isDefined(customChargeRateBoost);
  const excludedLevelsForPreSelect = [Severity.ERROR, Severity.WARN];
  const initialSelectedShiftInvites = [...multipleShiftInfo.keys()].filter(
    (shiftInfoKey) =>
      !excludedLevelsForPreSelect.includes(
        multipleShiftValidationDetails.get(shiftInfoKey)?.level ?? Severity.INFO
      )
  );
  const [visibilityModel, setVisibilityModel] = useState<GridColumnVisibilityModel>({
    adjustedHourlyCharge: customChargeRateBoostIsDefined,
  });
  const [selectedInvites, setSelectedInvites] = useState<GridRowId[]>(initialSelectedShiftInvites);
  const [failedInvites, setFailedInvites] = useState<ShiftInviteInfo[]>([]);
  const multiShiftInviteErrorDialogState = useModalState();
  const spendLimitReachedModalState = useModalState();
  const [additionalHoursNeeded, setAdditionalHoursNeeded] = useState(0);
  const [spendLimitShifts, setSpendLimitShifts] = useState<SpendLimitReachedShift[]>([]);
  const validateSpendingControl = useValidateSpendingControl();
  const isShiftInviteCustomRateEnabled = useCbhFlag(CbhFeatureFlag.SHIFT_INVITE_CUSTOM_RATE_BOOST, {
    defaultValue: false,
  });
  const spendingControlsConfig = useCbhFlag(CbhFeatureFlag.CONFIGURE_SPENDING_CONTROLS, {
    defaultValue: { enabled: false },
  });
  const isSpendingControlsEnabled = spendingControlsConfig.enabled;

  const dataGridApiRef = useGridApiRef();

  useEffect(() => {
    void dataGridApiRef.current?.autosizeColumns();
  }, [modalState, dataGridApiRef]);

  const columns: GridColDef[] = [
    {
      field: "shiftTime",
      headerName: "Shift Time",
      minWidth: 400,
      colSpan: (params) => {
        if (params.row.type === "section-header") {
          return 3;
        }
        return 1;
      },
      renderCell: (params) => {
        if (params.row.type === "section-header") {
          return (
            <Text variant="body1" bold className="section-header">
              {params.row.name}
            </Text>
          );
        }

        const { message, level } = params.value;

        return (
          <Text
            variant="subtitle2"
            color={(theme) => {
              switch (level) {
                case Severity.ERROR:
                  return theme.palette.text.secondary;
                default:
                  return theme.palette.text.primary;
              }
            }}
          >
            {message ?? ""}
          </Text>
        );
      },
      resizable: false,
      sortable: false,
      disableColumnMenu: true,
      disableReorder: true,
    },
    {
      field: "additionalNotes",
      headerName: "Additional Notes",
      minWidth: 400,
      renderCell: (params) => {
        if (!params.value) {
          return null;
        }
        const { items } = params.value;

        if (!items || items.length === 0) {
          return null;
        }

        const combinedTooltip = items
          .map((item: { tooltip?: string; message: string }) => item.tooltip ?? item.message)
          .join("\n\n");

        return (
          <Tooltip
            onMouseEnter={() => {
              trackUserJourneyHoverStart();
            }}
            onMouseLeave={() => {
              addUserJourneyHoverEndEvent("Additional Notes Tooltip (Shift Invite Review)");
            }}
            title={
              <Text variant="subtitle2" sx={{ whiteSpace: "pre-line", color: "white" }}>
                {combinedTooltip}
              </Text>
            }
          >
            <Stack spacing={0.5}>
              {items.map((item: { message: string; level: string }) => (
                <Text
                  key={item.message}
                  variant="subtitle2"
                  color={(theme) => {
                    switch (item.level) {
                      case Severity.ERROR:
                        return theme.palette.error.main;
                      case Severity.WARN:
                        return theme.palette.warning.main;
                      default:
                        return theme.palette.text.primary;
                    }
                  }}
                  sx={{
                    borderBottom: "1px dotted currentColor",
                  }}
                >
                  {item.message}
                </Text>
              ))}
            </Stack>
          </Tooltip>
        );
      },
      sortable: false,
      disableColumnMenu: true,
      disableReorder: true,
    },
    {
      field: "adjustedHourlyCharge",
      headerName: "Adjusted Hourly Charge",
      minWidth: 200,
      renderCell: (params) => {
        if (!params.value) {
          return null;
        }
        const result = getDisplayChargeAndTooltip(params.value, customChargeRateBoostIsDefined);
        if (!isDefined(result)) {
          return null;
        }
        const { displayCharge, tooltip } = result;
        return (
          <Tooltip
            onMouseEnter={() => {
              trackUserJourneyHoverStart();
            }}
            onMouseLeave={() => {
              addUserJourneyHoverEndEvent("Adjusted Hourly Charge Tooltip (Shift Invite Review)");
            }}
            title={
              <Text variant="subtitle2" sx={{ whiteSpace: "pre", color: "white" }}>
                {tooltip}
              </Text>
            }
          >
            <Box>
              <Text
                variant="subtitle2"
                color={(theme) => theme.palette.text.primary}
                sx={{ borderBottom: "1px dotted #00000070", height: "1.5em" }}
              >
                {displayCharge}
              </Text>
            </Box>
          </Tooltip>
        );
      },
      sortable: false,
      disableColumnMenu: true,
      disableReorder: true,
    },
  ];

  const sortedShiftInfoEntries = Array.from(multipleShiftInfo.entries()).sort(
    (shiftInfoA, shiftInfoB) => {
      return shiftInfoA[1].start.getTime() - shiftInfoB[1].start.getTime();
    }
  );

  const allRows: GridRowsProp = sortedShiftInfoEntries.map(([shiftInfoKey, invite]) => {
    const shiftTime = `${
      formatDate(invite.start).split(",")[0]
    } ${invite.name?.toUpperCase()} shift, ${formatTime(invite.start)} - ${formatTime(invite.end)}${
      isSameDay(invite.start, invite.end) ? "" : " (next day)"
    }`;
    const validationDetails = multipleShiftValidationDetails.get(shiftInfoKey);
    const checkShiftInviteDetails = multipleCheckShiftInviteDetails.get(shiftInfoKey);
    return {
      id: shiftInfoKey,
      shiftTime: {
        message: shiftTime,
        level: validationDetails?.level,
      },
      additionalNotes: validationDetails
        ? {
            tooltip: validationDetails.tooltip,
            message: validationDetails.message,
            level: validationDetails.level,
            items: validationDetails.items,
          }
        : undefined,
      adjustedHourlyCharge: checkShiftInviteDetails ?? undefined,
      enabled: validationDetails?.level !== Severity.ERROR,
    };
  });

  const segmentProperties = {
    workerType: groupInviteSharedData.agentReq,
    ...(admin ? { adminUserId: user?._id } : { workplaceUserId: user?._id }),
    ...groupInviteSharedData,
  };
  const disableCheckboxHeader = allRows.every((row) => !row.enabled);
  const SEND_MULTIPLE_INVITES = "Send Multiple Invites";

  async function sendInvite(params: SendInviteParams) {
    const { shiftInfoKey, shiftInviteRequest, queryParams } = params;
    const shiftValidationDetails = multipleShiftValidationDetails.get(shiftInfoKey);
    const workerMissingDocuments = shiftValidationDetails?.missingDocuments;
    try {
      await createShiftInvite({ shiftInviteRequest, queryParams });
      logEvent(HCF_USER_EVENTS.SHIFT_INVITE, {
        action: "GROUP_INVITE_SENT",
        groupId: shiftInviteRequest.groupId,
        skipFacilityRequiredDocumentCheck:
          isDefined(workerMissingDocuments) &&
          !isEmpty(workerMissingDocuments) &&
          shiftValidationDetails?.level === Severity.WARN,
        workerMissingDocuments,
        isOverworkingShiftsAllowed: shiftValidationDetails?.isOverworkingShift,
        ...segmentProperties,
      });
      return { invite: shiftInviteRequest };
    } catch (error: unknown) {
      logError("Error while creating the group shift invite", {
        error,
        metadata: {
          invite: { shiftInviteRequest, queryParams },
        },
      });

      logEvent(HCF_USER_EVENTS.SHIFT_INVITE, {
        action: "GROUP_INVITE_SEND_ERROR",
        groupId: shiftInviteRequest.groupId,
        ...segmentProperties,
      });

      return { invite: shiftInviteRequest, error };
    }
  }

  function handleRequestApproval() {
    spendLimitReachedModalState.closeModal();
    modalState.closeModal();
  }

  function handleSpendLimitCancel() {
    spendLimitReachedModalState.closeModal();
  }

  const rowsWithIssues = allRows.filter((row) => row.additionalNotes?.level);
  const rowsWithoutIssues = allRows.filter((row) => !row.additionalNotes?.level);

  const hasIssues = rowsWithIssues.length > 0;
  const hasInvitesWithoutIssues = rowsWithoutIssues.length > 0;

  const needsReviewHeaderRow = {
    id: "needs-review-header",
    type: "section-header",
    name: "Needs Review",
  };

  const readyToSendHeaderRow = {
    id: "ready-to-send-header",
    type: "section-header",
    name: "Ready to Send",
  };

  const rows = hasIssues
    ? [
        needsReviewHeaderRow,
        ...rowsWithIssues,
        ...(hasInvitesWithoutIssues ? [readyToSendHeaderRow, ...rowsWithoutIssues] : []),
      ]
    : allRows;

  return (
    <>
      <Box sx={{ height: 400 }}>
        <DataGridPro
          apiRef={dataGridApiRef}
          rows={rows}
          columns={columns}
          autosizeOnMount
          checkboxSelection
          rowSelectionModel={selectedInvites}
          getRowHeight={(params) => (params.model.type === "section-header" ? undefined : "auto")}
          onRowSelectionModelChange={(newSelectionModel) => {
            addUserJourneyEvent(
              isEmpty(newSelectionModel) ? "deselect" : "select",
              "Shift Invite Review Selection"
            );
            setSelectedInvites(newSelectionModel);
          }}
          pagination={false}
          hideFooterRowCount={isShiftInviteCustomRateEnabled}
          getRowClassName={(params) => (params.row.enabled ? "" : "Mui-disabled")}
          isRowSelectable={(params) => params.row.enabled}
          disableRowSelectionOnClick
          columnVisibilityModel={visibilityModel}
          onColumnVisibilityModelChange={(newModel) => setVisibilityModel(newModel)}
          localeText={{
            footerTotalRows: "Total Invite Count",
            footerRowSelected(count) {
              return `You have selected ${count} ${pluralize("invite", count)}`;
            },
          }}
          sx={{
            ".MuiDataGrid-columnSeparator": {
              display: "none",
            },
            ".MuiDataGrid-columnHeaders": {
              backgroundColor: "#F5F5F5",
              color: (theme) => theme.palette.secondary.main,
            },
            "& .MuiDataGrid-columnHeader:focus-within": {
              outline: "none",
            },
            "& .MuiDataGrid-cell": {
              whiteSpace: "normal",
              overflow: "visible",
              alignItems: "center",
            },
            // Section headers
            "& [data-id='needs-review-header'], & [data-id='ready-to-send-header']": {
              width: "100%",
              // Hide the checkbox
              "& .MuiDataGrid-cellCheckbox": {
                display: "none",
              },
              // Add a border to the whole row and hide it on the cells since the
              // width of the cells doesn't fill the row when the checkbox is hidden
              borderBottom: "1px solid #e0e0e0",
              "& .MuiDataGrid-cell": {
                borderBottom: "none",
              },
              // Remove hover effect
              "&:hover": {
                backgroundColor: "unset",
              },
            },
            ...(disableCheckboxHeader && {
              "& .MuiDataGrid-columnHeaderCheckbox .MuiDataGrid-columnHeaderTitleContainer": {
                display: "none",
              },
            }),
          }}
        />
      </Box>
      <Stack spacing={1} direction="row" justifyContent="flex-end">
        <Button
          onClick={() => {
            setActiveStep(1);
            addUserJourneyEvent("click", "Back Button (Shift Invite Review)");
            logEvent(HCF_USER_EVENTS.SHIFT_INVITE, {
              action: "GROUP_INVITE_BACK_TO_DETAILS_SECTION",
              ...segmentProperties,
            });
          }}
          variant="outlined"
          disabled={isSaving}
          startIcon={<ArrowBackIosNewOutlinedIcon />}
        >
          Back
        </Button>
        <span>
          <Button
            variant="contained"
            disabled={isSaving || validateSpendingControl.isLoading || selectedInvites.length === 0}
            startIcon={
              (isSaving || validateSpendingControl.isLoading) && (
                <CircularProgress size={18} sx={{ color: "inherit" }} />
              )
            }
            onClick={async () => {
              addUserJourneyEvent("click", SEND_MULTIPLE_INVITES);
              const dataSource = new Map(selectedInvites.map((id) => [id, true]));

              // Build array of selected shifts for validation
              const selectedShifts = [...multipleShiftInfo.entries()]
                .filter(([shiftInfoKey]) => dataSource.has(shiftInfoKey))
                .map(([, invite]) => invite);

              // Spending control validation - applies to all users when enabled
              if (isSpendingControlsEnabled) {
                try {
                  const shiftsForValidation: SpendLimitReachedShift[] = selectedShifts.map(
                    (invite) => ({
                      start: invite.start,
                      end: invite.end,
                      requirement: workerType,
                      timeSlot: toShiftType(invite.name),
                    })
                  );

                  const validationResult = await validateSpendingControl.mutateAsync({
                    workplaceId: groupInviteSharedData.workplaceId,
                    shifts: shiftsForValidation,
                  });

                  if (!validationResult.valid) {
                    const totalHours = selectedShifts.reduce((sum, invite) => {
                      return sum + Math.ceil(differenceInMinutes(invite.end, invite.start) / 60);
                    }, 0);
                    setAdditionalHoursNeeded(totalHours);
                    setSpendLimitShifts(shiftsForValidation);
                    spendLimitReachedModalState.openModal();
                    return;
                  }
                } catch {
                  // Fail-open: allow invite creation if validation API fails
                  // Error is logged by the useValidateSpendingControl hook
                }
              }

              const groupId = uuidv4();

              const createShiftInviteRequests = [...multipleShiftInfo.entries()]
                .filter(([shiftInfoKey, _invite]) => {
                  return dataSource.has(shiftInfoKey);
                })
                .map(([shiftInfoKey, invite]) => {
                  const shiftInviteRequest = {
                    workerId: selectedWorker?.workerId ?? "",
                    workplaceId: groupInviteSharedData.workplaceId,
                    shiftDetails: {
                      start: zonedTimeToUtc(invite.start, facilityTimeZone),
                      end: zonedTimeToUtc(invite.end, facilityTimeZone),
                      agentReq: workerType,
                      name: invite.name,
                      tmz: facilityTimeZone,
                      customChargeRateBoost,
                    },
                    postShiftToMarketplace: groupInviteSharedData.postShiftToMarketplace,
                    groupId,
                  };
                  return sendInvite({
                    shiftInviteRequest,
                    queryParams: {
                      isOverworkingShiftsAllowed: true,
                      skipFacilityRequiredDocumentCheck: true,
                    },
                    shiftInfoKey,
                  });
                });

              const results = await Promise.allSettled(createShiftInviteRequests);

              const failed = results.reduce((acc, result) => {
                if (result.status === "fulfilled" && isDefined(result.value.error)) {
                  acc.push({
                    ...result.value.invite.shiftDetails,
                    error: result.value.error,
                  });
                }
                return acc;
              }, [] as ShiftInviteInfo[]);

              if (failed.length > 0) {
                setFailedInvites(failed);
                logEvent(HCF_USER_EVENTS.SHIFT_INVITE, {
                  action: "GROUP_INVITE_SENT_PARTIAL_SUCCESSFUL",
                  totalInvites: createShiftInviteRequests.length,
                  failedInvites: failed.length,
                  groupId,
                  ...segmentProperties,
                });
                multiShiftInviteErrorDialogState.openModal();
                return;
              }

              toast.success(
                `You sent ${createShiftInviteRequests.length} shift invites to ${selectedWorker?.fullName}. You can view the invitations on your calendar.`
              );

              logEvent(HCF_USER_EVENTS.SHIFT_INVITE, {
                action: "GROUP_INVITE_SENT_SUCCESSFUL",
                totalInvites: createShiftInviteRequests.length,
                groupId,
                ...segmentProperties,
              });

              logUserJourney("submit", SEND_MULTIPLE_INVITES);
              modalState.closeModal();
            }}
          >
            Send All Selected Invites
          </Button>
        </span>
      </Stack>
      <MultiShiftInviteErrorDialog
        modalState={multiShiftInviteErrorDialogState}
        failedInvites={failedInvites}
        onClose={() => {
          multiShiftInviteErrorDialogState.closeModal();
          modalState.closeModal();
        }}
      />
      {isSpendingControlsEnabled && (
        <BerlinThemeProvider>
          <SpendLimitReachedDialog
            modalState={spendLimitReachedModalState}
            workplaceId={groupInviteSharedData.workplaceId}
            shifts={spendLimitShifts}
            additionalHoursNeeded={additionalHoursNeeded}
            onAdditionalHoursChange={setAdditionalHoursNeeded}
            onRequestApproval={handleRequestApproval}
            onCancel={handleSpendLimitCancel}
            isLoading={validateSpendingControl.isLoading}
          />
        </BerlinThemeProvider>
      )}
    </>
  );
}
